import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import {
  FormControlLabel,
  Switch,
  useMediaQuery,
  debounce,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../../utility/apiInstance";
import { useNavigate } from "react-router-dom";
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridSlots,
  GridToolbarProps,
} from "@mui/x-data-grid";
// import { randomId } from "@mui/x-data-grid-generator";

import toast from "react-hot-toast";
import {
  hideToggleButton,
  hideToggleButtonEditMode,
  preventAddingDuplocateRow,
} from "../../utility/constant";
import clsx from "clsx";
import { HexColorPicker, HexColorInput } from "react-colorful";


function randomId() {
  return Math.random().toString(36).substr(2, 9);
}
const id = randomId();
function EditToolbar(props: EditToolbarProps) {
  const {
    setRows,
    setRowModesModel,
    currentPage,
    isAddDisabled,
    setIsAddDisabled,
  } = props;

  const handleClick = () => {
    const id = Math.random();
    setRows((oldRows) => {
      if (!preventAddingDuplocateRow(oldRows)) {
        toast.error("You can only submit one record at a time!");
        return oldRows;
      }

      setIsAddDisabled(true);

      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: "name" },
      }));

      return [
        {
          id,
          name: "",
          colorCode: "",
          description: "",
          isActive: true,
          isNew: true,
          ishideToggle: false,
        },
        ...oldRows,
      ];
    });
  };

  return currentPage === 0 ? (
    <GridToolbarContainer>
      <div className="buttonadd">
        <Button
          className={clsx("btnTheme btnPadding", {
            "opacity-50 cursor-not-allowed": isAddDisabled,
          })}
          onClick={handleClick}
          disabled={isAddDisabled}
        >
          Add tag
        </Button>
      </div>
    </GridToolbarContainer>
  ) : null;
}
interface EditToolbarProps extends GridToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
  currentPage: number;
  isAddDisabled: boolean;
  setIsAddDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

function ManageTags() {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<any>([]);
  const [rowdata, setRowdata] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );
  const [isAddDisabled, setIsAddDisabled] = React.useState(false);

  const handleLogout = (): void => {
    localStorage.clear();
    navigate("/");
  };

  const getAllTag = async () => {
    try {
      let response = await axiosInstance.get("/api/v1/tag");
      const fetchedRows = response?.data?.data?.reverse();
      const data = fetchedRows.map((row: any, index: any) => ({
        ...row,
        key: row?._id || row?.id,
        id: row?._id || row?.id,
        ishideToggle: true,
        isNew: false,
      }));
      setRows(data);
      if (response.data.statusCode === 401) {
        handleLogout();
      }
    } catch (err: any) {}
  };
  React.useEffect(() => {
    getAllTag();
  }, []);

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    setIsAddDisabled(true);
  };

  const handleSaveClick = (id: GridRowId) => async () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });

    setRows((prev: any) => {
      let array = [...prev];
      let findIndex = array.findIndex((item: any) => item?._id === id);
      if (findIndex !== -1) {
        array[findIndex].ishideToggle = false;
      }
      return array;
    });

    setIsAddDisabled(false);
  };

  const debouncedHandleDeleteClick = debounce(
    async (id: GridRowId, row: any) => {
      const updatedIsDeleted = !row.isActive;
      const payload = { isActive: updatedIsDeleted };
      const url = `/api/v1/tag/${id}`;

      try {
        const response = await axiosInstance.patch(url, payload);

        if (response.status !== 200) {
          toast.error("Error updating customer team status.");
        } else {
          const successMessage = updatedIsDeleted
            ? "Tag activated successfully."
            : "Tag deactivated successfully.";
          toast.success(successMessage);
          getAllTag();
        }
      } catch (error: any) {
        error?.response?.data?.error
          ? toast.error(error.response.data.error)
          : toast.error(
              "An error occurred while updating customer team status."
            );
      }
    },
    300
  );

  const handleDeleteClick = (id: GridRowId, row: any) => async () => {
    debouncedHandleDeleteClick(id, row);
  };

  const handleCancelClick = (id: GridRowId) => async () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow: any = rows.find((row: any) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows.filter((row: any) => row.id !== id));
    }

    if (!editedRow.isNew) {
      setRows((prev: any) => {
        let array = [...prev];
        let findIndex = array.findIndex((item: any) => item?._id === id);
        if (findIndex !== -1) {
          array[findIndex].ishideToggle = true;
        }
        return array;
      });
    }

    setIsAddDisabled(false);
  };

  const processRowUpdate = async (newRow: GridRowModel, params: any) => {
    const updatedRow: any = { ...newRow, isNew: false };
    setRowdata(updatedRow);
    const errors = [];
    if (!updatedRow.name) {
      errors.push("Tag name cannot be empty");
      setIsAddDisabled(true);
    }
    if (!updatedRow.description) {
      errors.push("Tag description cannot be empty");
      setIsAddDisabled(true);
    }
    if (!updatedRow.colorCode) {
      errors.push("Tag color cannot be empty");
      setIsAddDisabled(true);
    }
    if (errors.length > 0) {
      if (
        updatedRow.id ===
        rows?.find((item: any) => item._id === updatedRow._id)?._id
      ) {
        setRows(hideToggleButton(rows, updatedRow.id));
      }

      errors.forEach((error) => toast.error(error));
      return;
    }

    if (+updatedRow.id < 1) {
      updatedRow.id = id;

      const formData = {
        name: updatedRow.name,
        description: updatedRow.description,
        colorCode: updatedRow.colorCode,
        isActive: true,
      };
      try {
        const response = await axiosInstance.post("/api/v1/tag", formData);
        const data = response.data;
        toast.success("Tag created successfully");
        getAllTag();
        setRows((prevRows: any) =>
          prevRows.map((row: any, index: number) =>
            row.id === newRow.id ? updatedRow : row
          )
        );
      } catch (err: any) {
        if (
          updatedRow.id ===
          rows?.find((item: any) => item._id === updatedRow._id)?._id
        ) {
          setRows(hideToggleButtonEditMode(rows, updatedRow));
        }

        Array?.isArray(err?.response?.data?.error)
          ? err?.response?.data?.error?.forEach((error: any) => {
              toast.error(`${error.field}: ${error.message}`);
            })
          : toast?.error(err.response.data.error) ||
            toast?.error("An error occurred during registration.");
        getAllTag();
        return updatedRow;
      }
    } else {
      const sendData = {
        name: updatedRow.name,
        description: updatedRow.description,
        colorCode: updatedRow.colorCode,
        isActive: true,
      };

      const url = `/api/v1/tag/${updatedRow._id}`;
      try {
        const response = await axiosInstance.patch(url, sendData);
        const data = response.data;

        setRows((prev: any) => {
          let array = [...prev];
          let findIndex = array.findIndex(
            (item: any) => item?.id === response?.data?.data?._id
          );
          if (findIndex !== -1) {
            array[findIndex] = { ...array[findIndex], ...updatedRow };
          }
          return array;
        });

        toast.success(data?.message);
        getAllTag();

        return updatedRow;
      } catch (err: any) {
        if (
          updatedRow.id ===
          rows?.find((item: any) => item._id === updatedRow._id)?._id
        ) {
          setRows((prevRows: any) =>
            hideToggleButtonEditMode(prevRows, updatedRow)
          );
        }

        if (Array.isArray(err.response?.data?.error)) {
          err.response.data.error.forEach((error: any) => {
            toast.error(`${error.field}: ${error.message}`);
          });
        } else {
          err.response?.data?.error
            ? toast.error(err.response?.data?.error)
            : toast.error("An error occurred during customer team update.");
        }
        setRows((prevRows: any) =>
          hideToggleButtonEditMode(prevRows, updatedRow)
        );
      }
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const handlePaginationChange = (paginationModel: any) => {
    setCurrentPage(paginationModel.page);
  };

  const columns: GridColDef[] = [
    {
      field: "s_no",
     headerName: "S.No.",
      width: 70,
      sortable: true,
      filterable: false,
      editable: false,
      disableColumnMenu: true,
    },
    {
      field: "name",
      headerName: "Tag Name",
      width: isMobile ? 120 : 250,
      sortable: true,
      filterable: true,
      editable: true,
    },
    {
      field: "colorCode",
      headerName: "Tag Color",
      width: 210,
      sortable: true,
      filterable: true,
      editable: true,
      renderEditCell: (params) => {
        const handleChange = (newColor: string) => {
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newColor,
          });
        };

        return (
          <div
            style={{
              width: "100%",
              padding: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              paddingLeft: "15px",
              paddingRight: "15px",
            }}
          >
            <HexColorPicker
              color={params.value || "#000000"}
              onChange={handleChange}
              style={{ width: "100%" }}
            />
            <HexColorInput
              color={params.value || "#000000"}
              onChange={handleChange}
              prefixed
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        );
      },
      renderCell: (params) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            <div
              style={{
                width: "122px",
                height: "33px",
                backgroundColor: params.value || "transparent",
                border: "1px solid #ccc",
                marginRight: "8px",
                borderRadius: "4px",
              }}
            />

            <span
              style={{
                fontFamily: "monospace",
                color: "white",
                position: "absolute",
                top: 0,
                left: 0,
                width: "122px",
                height: "33px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {params.value}
            </span>
          </div>
        );
      },
    },

    {
      field: "description",
      headerName: "Tag Description",
      width: 400,
      sortable: true,
      filterable: true,
      editable: true,
      cellClassName: "new-row-border",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 170,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`save-${id}`}
              icon={<SaveIcon />}
              label="Save"
              style={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              key={`cancel-${id}`}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        if (!row?.ishideToggle) {
          return [<></>];
        }
        return [
          <GridActionsCellItem
            disabled={!row.isActive}
            icon={<EditIcon />}
            key={`edit-${id}`}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key={`delete-${id}`}
            icon={
              <FormControlLabel
                control={
                  <Switch
                    checked={row.isActive}
                    sx={(theme) => ({
                      "& .MuiSwitch-switchBase": {
                        "&.Mui-checked": {
                          color: "green",
                          "& + .MuiSwitch-track": {
                            backgroundColor: "green",
                          },
                        },
                        "&.Mui-unchecked": {
                          color: "#ff0000",
                          "& + .MuiSwitch-track": {
                            backgroundColor: "red",
                          },
                        },
                      },
                    })}
                  />
                }
                label=""
              />
            }
            label="Delete"
            onClick={handleDeleteClick(id, row)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <>
      <div className="old_dashboard_title">
        <h1 className="text-2xl font-bold text-gray-700 mt-[-15px] mb-[10px]">
          Manage Tags
        </h1>
      </div>
      <div className="whiteBox">
        <Box
          sx={{
            height: 660, 
            width: "100%",
            "& .actions": {
              color: "text.secondary",
            },
            "& .textPrimary": {
              color: "text.primary",
            },
            "& .new-row-border": {
              border: "2px solid #e7ebef",
              textAlign: "center",
            },
            // boxShadow: "rgba(0, 0, 0, 0.20) 0px 3px 10px",
          }}
        >
          <DataGrid
            rows={rows.map((row: any, index: any) => ({
              ...row,
              key: row?._id || row.id,
              id: row?._id || row.id,
              name: row?.name,
              description: row?.description,
              colorCode: row?.colorCode,
              isActive: row?.isActive,
              s_no: index + 1,
            }))}
            columns={columns}
            getRowHeight={() => "auto"}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onPaginationModelChange={handlePaginationChange}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10]}
            getRowClassName={(params) =>
              params.row.isNew ? "new-row-border" : ""
            }
            getRowId={(rows) => rows.id}
            slots={{
              toolbar: EditToolbar as GridSlots["toolbar"],
            }}
            slotProps={{
              toolbar: {
                setRows,
                setRowModesModel,
                currentPage,
                isAddDisabled,
                setIsAddDisabled,
              } as GridToolbarProps,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        </Box>
      </div>
    </>
  );
}

export default ManageTags;
